import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MonthYearPicker, SchoolPicker } from "../components/Filters";
import { MONTHS, formatTanggalMini, weekdayName, formatDateTime } from "../lib/format";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Save, RotateCcw, Copy, Printer, Download, Plus, FileText, Lock, Unlock,
  Loader2, StickyNote, CalendarPlus, Trash2, CheckCircle2,
} from "lucide-react";

const LABEL_STYLES = {
  normal: "",
  libur: "bg-rose-100 text-rose-700",
  dibatalkan: "bg-gray-200 text-gray-600",
  pengganti: "bg-amber-100 text-amber-700",
};
const LABEL_TEXT = { libur: "Libur", dibatalkan: "Dibatalkan", pengganti: "Pengganti" };

export default function Attendance({ notesMode }) {
  const { user } = useAuth();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // key -> partial cell
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);

  const [dateDialog, setDateDialog] = useState(null); // teaching date obj
  const [cellDialog, setCellDialog] = useState(null); // {teacher, date}
  const [addDialog, setAddDialog] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newLabel, setNewLabel] = useState("normal");

  const isCoord = user?.role === "coordinator";

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data);
      if (r.data.length && !schoolId) setSchoolId(r.data[0].id);
    });
  }, []);

  const load = () => {
    if (!schoolId) return;
    setLoading(true);
    api
      .get(`/attendance?year=${year}&month=${month}&school_id=${schoolId}`)
      .then((r) => { setData(r.data); setEdits({}); })
      .catch(() => toast.error("Gagal memuat data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [year, month, schoolId]);

  const key = (tid, d) => `${tid}__${d}`;

  const getCell = (tid, d) => {
    const k = key(tid, d);
    const base = data?.rows.find((r) => r.teacher_id === tid)?.cells[d] || {};
    return { ...base, ...(edits[k] || {}) };
  };

  const setSessions = (tid, d, val) => {
    const n = Math.max(0, parseInt(val || "0", 10) || 0);
    const k = key(tid, d);
    setEdits((prev) => ({ ...prev, [k]: { ...getCell(tid, d), sessions: n } }));
  };

  const rowTotal = (tid) => {
    if (!data) return 0;
    return data.dates.reduce((sum, dt) => sum + (getCell(tid, dt.date).sessions || 0), 0);
  };

  const dirtyKeys = Object.keys(edits);
  const locked = data?.locked;
  const status = data?.status || "draft";
  const isFinal = status === "final" || status === "locked";
  const editable = !isFinal && !(isCoord && status !== "draft");

  const changeStatus = async (newStatus) => {
    try {
      await api.post("/report-status", { school_id: schoolId, year, month, status: newStatus });
      toast.success("Status laporan diperbarui");
      setConfirmStatus(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah status"); }
  };

  const saveAll = async () => {
    if (!dirtyKeys.length) { toast.info("Tidak ada perubahan"); return; }
    setSaving(true);
    const cells = dirtyKeys.map((k) => {
      const [tid, d] = k.split("__");
      const c = getCell(tid, d);
      return {
        teacher_id: tid, date: d, sessions: c.sessions || 0,
        material: c.material || null, class_group: c.class_group || null,
        start_time: c.start_time || null, end_time: c.end_time || null,
        note: c.note || null, substitute_note: c.substitute_note || null,
      };
    });
    try {
      await api.post("/attendance/save", { year, month, school_id: schoolId, cells });
      toast.success(`${cells.length} data tersimpan`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const doCopy = async () => {
    setConfirmCopy(false);
    try {
      const { data: res } = await api.post("/attendance/copy-previous", { year, month, school_id: schoolId });
      toast.success(`${res.copied} sesi disalin dari bulan sebelumnya`);
      load();
    } catch (e) { toast.error("Gagal menyalin"); }
  };

  const saveDateLabel = async () => {
    try {
      await api.put(`/teaching-dates/${dateDialog.id}`, { label: dateDialog.label, note: dateDialog.note });
      toast.success("Status tanggal diperbarui");
      setDateDialog(null); load();
    } catch (e) { toast.error("Gagal memperbarui"); }
  };

  const addSpecialDate = async () => {
    if (!newDate) return;
    try {
      await api.post("/teaching-dates", { school_id: schoolId, date: newDate, label: newLabel });
      toast.success("Tanggal ditambahkan");
      setAddDialog(false); setNewDate(""); setNewLabel("normal"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menambah tanggal"); }
  };

  const deleteDate = async (dt) => {
    try {
      await api.delete(`/teaching-dates/${dt.id}`);
      toast.success("Tanggal dihapus");
      setDateDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const saveCellDetail = () => {
    const { teacher_id, date } = cellDialog;
    const c = getCell(teacher_id, date);
    setEdits((prev) => ({ ...prev, [key(teacher_id, date)]: c }));
    setCellDialog(null);
    toast.info("Detail diisi. Klik 'Simpan Semua' untuk menyimpan.");
  };

  const updateCellField = (field, value) => {
    const { teacher_id, date } = cellDialog;
    setEdits((prev) => ({ ...prev, [key(teacher_id, date)]: { ...getCell(teacher_id, date), [field]: value } }));
  };

  const exportCSV = () => {
    if (!data) return;
    const header = ["Nama Guru", ...data.dates.map((d) => formatTanggalMini(d.date)), "Total Sesi"];
    const rows = data.rows.map((r) => [
      r.teacher_name, ...data.dates.map((d) => getCell(r.teacher_id, d.date).sessions || 0), rowTotal(r.teacher_id),
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Rekap-Absensi-${data.school.name}-${MONTHS[month - 1]}-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cellNow = cellDialog ? getCell(cellDialog.teacher_id, cellDialog.date) : {};

  return (
    <div className="space-y-5 min-w-0" data-testid="attendance-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">
            {notesMode ? "Catatan Mengajar" : "Absensi & Teaching Record"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {data?.school?.name} · {data?.school?.day_label} · {MONTHS[month - 1]} {year}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <SchoolPicker schools={schools} schoolId={schoolId} setSchoolId={setSchoolId} />
          <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
        </div>
      </div>

      {/* status + toolbar */}
      <div className="flex flex-wrap items-center gap-2 no-print">
        <Badge className={`rounded-full gap-1 ${status === "final" ? "bg-gtsnavy text-white" : status === "checked" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`} data-testid="report-status">
          {status === "final" ? <><Lock className="w-3 h-3" /> Final / Dikunci</> : status === "checked" ? <><CheckCircle2 className="w-3 h-3" /> Diperiksa</> : "Draft"}
        </Badge>
        {!isCoord && status === "draft" && (
          <Button size="sm" variant="outline" onClick={() => changeStatus("checked")} className="rounded-full h-7 text-xs gap-1" data-testid="mark-checked-btn"><CheckCircle2 className="w-3 h-3" /> Tandai Diperiksa</Button>
        )}
        {!isCoord && status === "checked" && (
          <>
            <Button size="sm" onClick={() => setConfirmStatus("final")} className="rounded-full h-7 text-xs bg-gtsnavy text-white gap-1" data-testid="lock-btn"><Lock className="w-3 h-3" /> Kunci Final</Button>
            <Button size="sm" variant="outline" onClick={() => changeStatus("draft")} className="rounded-full h-7 text-xs" data-testid="back-draft-btn">Kembali ke Draft</Button>
          </>
        )}
        {!isCoord && status === "final" && (
          <Button size="sm" variant="outline" onClick={() => setConfirmStatus("draft")} className="rounded-full h-7 text-xs text-rose-600 border-rose-200 gap-1" data-testid="unlock-btn"><Unlock className="w-3 h-3" /> Buka Kunci</Button>
        )}
        {isCoord && status !== "draft" && (
          <span className="text-xs text-muted-foreground">Laporan terkunci untuk diedit</span>
        )}
        {dirtyKeys.length > 0 && (
          <Badge className="bg-rose-100 text-rose-700 rounded-full" data-testid="unsaved-badge">{dirtyKeys.length} belum disimpan</Badge>
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={saveAll} disabled={saving || !editable || !dirtyKeys.length}
          className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="save-all-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Semua
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEdits({})} disabled={!dirtyKeys.length} className="rounded-full gap-1" data-testid="reset-btn">
          <RotateCcw className="w-4 h-4" /> Reset
        </Button>
        <Button size="sm" variant="outline" onClick={() => setConfirmCopy(true)} disabled={!editable} className="rounded-full gap-1" data-testid="copy-prev-btn">
          <Copy className="w-4 h-4" /> Salin Bulan Lalu
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAddDialog(true)} disabled={!editable} className="rounded-full gap-1" data-testid="add-date-btn">
          <CalendarPlus className="w-4 h-4" /> Tanggal Khusus
        </Button>
        <Button size="sm" variant="outline" onClick={exportCSV} className="rounded-full gap-1" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.print()} className="rounded-full gap-1" data-testid="print-btn">
          <Printer className="w-4 h-4" /> Print
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(16,42,86,0.06)] overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-7 h-7 text-gtsorange animate-spin" /></div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">Tidak ada guru pada sekolah ini.</div>
        ) : (
          <div className="overflow-x-auto gts-scroll" data-testid="attendance-table-wrap">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gtsnavy text-white">
                  <th className="sticky left-0 z-10 bg-gtsnavy text-left px-4 py-3 font-semibold min-w-[180px] sticky-col-shadow">Nama Guru</th>
                  {data.dates.map((d) => (
                    <th key={d.date} className="px-3 py-2 text-center font-semibold min-w-[76px]">
                      <button onClick={() => setDateDialog({ ...d })} className="flex flex-col items-center gap-0.5 w-full" data-testid={`date-header-${d.date}`}>
                        <span className="text-[11px] text-white/60">{weekdayName(d.date)}</span>
                        <span>{formatTanggalMini(d.date)}</span>
                        {d.label !== "normal" && (
                          <span className={`text-[9px] px-1.5 rounded-full ${LABEL_STYLES[d.label]}`}>{LABEL_TEXT[d.label]}</span>
                        )}
                        {d.is_special && <span className="text-[9px] text-gtsorange-light">khusus</span>}
                      </button>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center font-semibold bg-gtsorange min-w-[90px]">Total Sesi</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => (
                  <tr key={r.teacher_id} className={`${idx % 2 ? "bg-orange-50/30" : "bg-white"} hover:bg-orange-50 transition-colors`}>
                    <td className={`sticky left-0 z-10 px-4 py-2 font-medium text-gtsnavy sticky-col-shadow ${idx % 2 ? "bg-orange-50/60" : "bg-white"}`}>
                      {r.teacher_name}
                    </td>
                    {data.dates.map((d) => {
                      const c = getCell(r.teacher_id, d.date);
                      const disabledCell = !editable || d.label === "libur" || d.label === "dibatalkan";
                      const hasDetail = c.material || c.class_group || c.note || c.start_time || c.substitute_note;
                      return (
                        <td key={d.date} className="px-2 py-2 text-center align-middle">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="number" min="0" value={c.sessions || 0}
                              disabled={disabledCell}
                              onChange={(e) => setSessions(r.teacher_id, d.date, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className={`w-12 h-9 text-center rounded-lg border font-semibold outline-none focus:ring-2 focus:ring-gtsorange ${
                                (c.sessions || 0) > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200"
                              } ${disabledCell ? "opacity-40 cursor-not-allowed" : ""}`}
                              data-testid={`cell-${r.teacher_id}-${d.date}`}
                            />
                            <button
                              onClick={() => setCellDialog({ teacher_id: r.teacher_id, teacher_name: r.teacher_name, date: d.date })}
                              disabled={!editable}
                              className={`text-[10px] flex items-center gap-0.5 ${hasDetail ? "text-gtsorange" : "text-gray-300"} hover:text-gtsorange`}
                              data-testid={`cell-note-${r.teacher_id}-${d.date}`}
                            >
                              <StickyNote className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-center font-nunito font-extrabold text-gtsorange text-lg" data-testid={`total-${r.teacher_id}`}>
                      {rowTotal(r.teacher_id)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        0 = tidak mengajar · 1 = satu sesi · 2 = dua sesi. Angka lebih dari 2 diperbolehkan.
      </p>

      {/* Copy confirm */}
      <AlertDialog open={confirmCopy} onOpenChange={setConfirmCopy}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Salin data bulan sebelumnya?</AlertDialogTitle>
            <AlertDialogDescription>
              Data sesi dari bulan sebelumnya akan disalin ke {MONTHS[month - 1]} {year} untuk sekolah ini. Data yang sudah ada bisa tertimpa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doCopy} data-testid="confirm-copy">Ya, Salin</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmStatus} onOpenChange={(o) => !o && setConfirmStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmStatus === "final" ? "Kunci laporan (Final)?" : "Buka kunci laporan?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmStatus === "final"
                ? "Setelah dikunci, Coordinator tidak dapat mengubah data absensi bulan ini. Branch Manager tetap dapat membuka kunci kembali."
                : "Laporan akan kembali ke status Draft dan dapat diedit kembali."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => changeStatus(confirmStatus)} data-testid="confirm-status">Ya, Lanjutkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Date label dialog */}
      <Dialog open={!!dateDialog} onOpenChange={(o) => !o && setDateDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Status Tanggal {dateDialog && formatTanggalMini(dateDialog.date)}</DialogTitle></DialogHeader>
          {dateDialog && (
            <div className="space-y-3">
              <div>
                <Label>Status</Label>
                <Select value={dateDialog.label} onValueChange={(v) => setDateDialog({ ...dateDialog, label: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="date-label-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="libur">Libur</SelectItem>
                    <SelectItem value="dibatalkan">Dibatalkan</SelectItem>
                    <SelectItem value="pengganti">Pertemuan Pengganti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Catatan alasan</Label>
                <Textarea value={dateDialog.note || ""} onChange={(e) => setDateDialog({ ...dateDialog, note: e.target.value })} className="rounded-xl mt-1" data-testid="date-note" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {dateDialog?.is_special && (
              <Button variant="outline" onClick={() => deleteDate(dateDialog)} className="rounded-full gap-1 text-rose-600 border-rose-200 mr-auto" data-testid="delete-date-btn">
                <Trash2 className="w-4 h-4" /> Hapus
              </Button>
            )}
            <Button onClick={saveDateLabel} className="rounded-full bg-gtsnavy text-white" data-testid="save-date-label">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add special date */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Tanggal Khusus</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tanggal</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl mt-1" data-testid="new-date-input" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={newLabel} onValueChange={setNewLabel}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="pengganti">Pertemuan Pengganti</SelectItem>
                  <SelectItem value="libur">Libur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addSpecialDate} className="rounded-full bg-gtsorange text-white" data-testid="save-new-date">Tambah</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cell detail dialog */}
      <Dialog open={!!cellDialog} onOpenChange={(o) => !o && setCellDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{cellDialog?.teacher_name} · {cellDialog && formatTanggalMini(cellDialog.date)}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Materi / Topik</Label>
              <Input value={cellNow.material || ""} onChange={(e) => updateCellField("material", e.target.value)} className="rounded-xl mt-1" data-testid="cell-material" />
            </div>
            <div>
              <Label>Kelas / Kelompok</Label>
              <Input value={cellNow.class_group || ""} onChange={(e) => updateCellField("class_group", e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>Sesi</Label>
              <Input type="number" min="0" value={cellNow.sessions || 0} onChange={(e) => updateCellField("sessions", Math.max(0, parseInt(e.target.value || "0", 10) || 0))} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>Jam Mulai</Label>
              <Input type="time" value={cellNow.start_time || ""} onChange={(e) => updateCellField("start_time", e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>Jam Selesai</Label>
              <Input type="time" value={cellNow.end_time || ""} onChange={(e) => updateCellField("end_time", e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Catatan Kegiatan</Label>
              <Textarea value={cellNow.note || ""} onChange={(e) => updateCellField("note", e.target.value)} className="rounded-xl mt-1" data-testid="cell-activity-note" />
            </div>
            <div className="col-span-2">
              <Label>Catatan Guru Pengganti</Label>
              <Input value={cellNow.substitute_note || ""} onChange={(e) => updateCellField("substitute_note", e.target.value)} className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCellDetail} className="rounded-full bg-gtsorange text-white" data-testid="save-cell-detail">Terapkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
